import type { ActivityWithCategory, Category } from "@/lib/types"

const CACHE_KEY = "activity-selector-data"

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">

export interface ActivitySelectorCache {
  categories: Category[]
  allActivities: ActivityWithCategory[]
}

const browserStorage = (): StorageLike | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage
  } catch {
    return null
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const isCategory = (value: unknown): value is Category =>
  isRecord(value)
  && typeof value._id === "string"
  && typeof value._creationTime === "number"
  && typeof value.displayName === "string"
  && typeof value.searchIndexHash === "string"
  && typeof value.colorHex === "string"
  && typeof value.sortOrder === "number"
  && typeof value.userId === "string"

const isActivity = (value: unknown): value is ActivityWithCategory =>
  isRecord(value)
  && typeof value._id === "string"
  && typeof value._creationTime === "number"
  && typeof value.displayName === "string"
  && typeof value.searchIndexHash === "string"
  && typeof value.categoryId === "string"
  && typeof value.userId === "string"
  && isCategory(value.category)
  && value.categoryId === value.category._id

const isActivitySelectorCache = (value: unknown): value is ActivitySelectorCache =>
  isRecord(value)
  && Array.isArray(value.categories)
  && value.categories.every(isCategory)
  && Array.isArray(value.allActivities)
  && value.allActivities.every(isActivity)

export function readActivitySelectorCache(
  storage: StorageLike | null = browserStorage()
): ActivitySelectorCache | null {
  if (!storage) return null

  try {
    const value = JSON.parse(storage.getItem(CACHE_KEY) ?? "null")
    return isActivitySelectorCache(value) ? value : null
  } catch {
    return null
  }
}

export function writeActivitySelectorCache(
  storage: StorageLike | null = browserStorage(),
  value: ActivitySelectorCache
) {
  try {
    storage?.setItem(CACHE_KEY, JSON.stringify(value))
  } catch {
    // Caching is optional; live Convex queries remain the fallback.
  }
}

export function clearActivitySelectorCache(
  storage: StorageLike | null = browserStorage()
) {
  try {
    storage?.removeItem(CACHE_KEY)
  } catch {
    // Caching is optional; live Convex queries remain the fallback.
  }
}
